/**
 * AgentsPage
 * Página principal de gestión de agentes
 */

import {
  Box,
  Alert,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from '@mui/material';
import ComputerIcon from '@mui/icons-material/Computer';
import AddIcon from '@mui/icons-material/Add';
import ShareIcon from '@mui/icons-material/Share';
import { ComputerSharingDialog } from '@/components/agents/ComputerSharingDialog';
import { useAgents } from '@/hooks/useAgents';
import { useGroups } from '@/hooks/useGroups';
import { useCreateGroup } from '@/hooks/useGroups';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAgentsStore } from '@/store/agentsStore';
import { AgentFilters } from '@/components/agents/AgentFilters';
import { AgentList } from '@/components/agents/AgentList';
import { AgentDetail } from '@/components/agents/AgentDetail';
import { GroupTree } from '@/components/groups/GroupTree';
import PageHeader from '@/components/common/PageHeader';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { agentsApi } from '@/lib/api/agents';
import { organizationsApi, type Organization } from '@/lib/api/organizations';
import useAuthStore from '@/store/authStore';

export const AgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [wsConnected, setWsConnected] = useState(false);
  const { isLoading, error } = useAgents();
  const { data: groups = [] } = useGroups('subgroups');
  const createGroup = useCreateGroup();
  const { getFilteredAgents, selectedAgentId, selectAgent, getSelectedAgent, agents } =
    useAgentsStore();

  // WebSocket for real-time updates
  useWebSocket({
    onOpen: () => setWsConnected(true),
    onClose: () => setWsConnected(false),
  });

  const filteredAgents = getFilteredAgents();
  const selectedAgent = getSelectedAgent();
  const detailOpen = !!selectedAgentId;
  const { filters } = useAgentsStore();
  const [connectIdInput, setConnectIdInput] = useState('');
  const [connectIdError, setConnectIdError] = useState('');
  const [claimOpen, setClaimOpen] = useState(false);
  const [sharingOpen, setSharingOpen] = useState(false);
  const [claimAgentId, setClaimAgentId] = useState('');
  const [claimPassword, setClaimPassword] = useState('');
  const [claimOrganizationId, setClaimOrganizationId] = useState('');
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [claimError, setClaimError] = useState('');
  const [claimBusy, setClaimBusy] = useState(false);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    void organizationsApi
      .list()
      .then((items) => {
        setOrganizations(items);
        setClaimOrganizationId((current) => current || items[0]?.id || '');
      })
      .catch(() => setOrganizations([]));
  }, []);

  const handleClaim = async () => {
    if (!claimAgentId.trim() || !claimPassword || !claimOrganizationId) return;
    setClaimBusy(true);
    setClaimError('');
    try {
      await agentsApi.claimAgent(claimAgentId.trim(), claimOrganizationId, claimPassword);
      setClaimOpen(false);
      setClaimAgentId('');
      setClaimPassword('');
    } catch (error) {
      setClaimError(error instanceof Error ? error.message : 'No se pudo reclamar el agente');
    } finally {
      setClaimBusy(false);
    }
  };

  const handleGenerateClaimPassword = async () => {
    if (!claimAgentId.trim()) {
      setClaimError('Ingresá primero el ID del agente');
      return;
    }
    setClaimBusy(true);
    setClaimError('');
    try {
      const result = await agentsApi.createClaimCredential(claimAgentId.trim());
      setClaimPassword(result.password);
    } catch (error) {
      setClaimError(error instanceof Error ? error.message : 'No se pudo crear la contraseña');
    } finally {
      setClaimBusy(false);
    }
  };

  const handleConnectById = () => {
    const needle = connectIdInput.replace(/\s+/g, '');
    if (!needle) return;
    const match = agents.find(
      (a) => (a.shortId || '').replace(/\s+/g, '') === needle || a.agentId === connectIdInput.trim()
    );
    if (!match) {
      setConnectIdError('No se encontró ningún equipo con ese ID');
      return;
    }
    setConnectIdError('');
    setConnectIdInput('');
    navigate(`/session/${match.agentId}`);
  };

  const handleAgentClick = (agentId: string) => {
    selectAgent(agentId);
  };

  const handleCloseDetail = () => {
    selectAgent(null);
  };

  const handleConnect = (agentId: string) => {
    navigate(`/session/${agentId}`);
  };

  const handleConsole = (agentId: string) => {
    navigate(`/terminal/${agentId}`);
  };

  const handleDetails = (agentId: string) => {
    selectAgent(agentId);
  };

  const handleCreateGroup = () => {
    const name = window.prompt('Nombre del grupo (Empresa):');
    if (!name || !name.trim()) return;
    createGroup.mutate({ name: name.trim() });
  };

  return (
    // Fill the remaining viewport height (set up by MainLayout's flex column
    // <main>) so the agent list scrolls internally and the body never does.
    // `flex: 1` + `minHeight: 0` lets this Box shrink below its content size
    // — without `minHeight: 0` flex items default to `min-height: auto` and
    // ignore `overflow: auto` on their children.
    <Box
      sx={{
        flex: 1,
        // `minWidth: 0` (paired with `minHeight: 0` below) lets this flex
        // column shrink below its children's intrinsic content size — both
        // horizontally (toolbar with minWidth:220 TextField, cards with
        // long UUIDs) and vertically (the agent list). Without these,
        // flex items default to `min-*: auto` and refuse to shrink below
        // their content, which on narrow viewports pushes the page wider
        // than the device. Combined with `<main>`'s `overflowX: 'hidden'`,
        // this guarantees nothing escapes the viewport on mobile.
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Page header (title, count, ws status) — pinned, no scroll */}
      <Box sx={{ flexShrink: 0 }}>
        <PageHeader
          icon={<ComputerIcon />}
          title="Agentes"
          subtitle={
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              {filteredAgents.length} agente{filteredAgents.length !== 1 ? 's' : ''}{' '}
              {filteredAgents.length === 1 ? 'encontrado' : 'encontrados'}
              {wsConnected && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label="Realtime conectado"
                  sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                />
              )}
            </Box>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>
            Error cargando agentes: {error instanceof Error ? error.message : 'Error desconocido'}
          </Alert>
        )}

        {/* Compact single-row toolbar: connect-by-id + sort + view toggles.
            `minWidth: 0` on the toolbar itself lets its flex children (the
            TextField with minWidth:220 + the AgentFilters cluster) wrap to
            a second line on narrow viewports instead of pushing past the
            page edge. Without it, flex items default to `min-width: auto`
            and the toolbar refuses to wrap, causing horizontal overflow. */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
            minWidth: 0,
            pb: 1.5,
            mb: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Conectar por ID (NNN NNN NNN)"
            value={connectIdInput}
            error={!!connectIdError}
            onChange={(e) => {
              setConnectIdInput(e.target.value);
              if (connectIdError) setConnectIdError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleConnectById();
            }}
            sx={{ minWidth: 220, flex: '1 1 220px', maxWidth: 360 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleConnectById}
            disabled={!connectIdInput.trim()}
          >
            Conectar
          </Button>
          {connectIdError && (
            <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', ml: 0.5 }}>
              {connectIdError}
            </Box>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreateGroup}
            disabled={createGroup.isPending}
            data-testid="agents-page-create-group"
            sx={{ textTransform: 'none' }}
          >
            Grupo
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setClaimOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Agregar agente
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ShareIcon />}
            onClick={() => setSharingOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Compartir computadora
          </Button>
          <Box sx={{ flex: '1 1 8px' }} /> {/* spacer pushes filters to the right */}
          <AgentFilters />
        </Box>
      </Box>

      {/* Scrollable region — only the list of agents scrolls.
          `overflowX: 'hidden'` blocks horizontal scroll entirely (some
          agent cards with very long hostnames/IDs could otherwise push
          past the viewport on small screens); `minWidth: 0` lets the
          flex item shrink below its intrinsic content width. */}
      <Box
        sx={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pr: 1 }}
      >
        {groups.length === 0 ? (
          <AgentList
            agents={filteredAgents}
            loading={isLoading}
            onAgentClick={handleAgentClick}
            onConnect={handleConnect}
            onConsole={handleConsole}
            onDetails={handleDetails}
            viewMode={filters.viewMode as any}
          />
        ) : (
          <GroupTree
            groups={groups}
            agents={filteredAgents}
            onAgentClick={handleAgentClick}
            onConnect={handleConnect}
            onConsole={handleConsole}
            onDetails={handleDetails}
            viewMode={filters.viewMode as any}
          />
        )}
      </Box>

      <AgentDetail agent={selectedAgent} open={detailOpen} onClose={handleCloseDetail} />
      {sharingOpen && (
        <ComputerSharingDialog
          agents={agents}
          canCreateUsers={user?.role === 'admin'}
          initialAgentId={selectedAgentId || undefined}
          onClose={() => setSharingOpen(false)}
          onClaim={(id) => {
            setSharingOpen(false);
            setClaimAgentId(id);
            setClaimOpen(true);
          }}
        />
      )}
      <Dialog
        open={claimOpen}
        onClose={() => !claimBusy && setClaimOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Agregar agente existente</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '12px !important' }}>
          <TextField
            label="ID del agente"
            value={claimAgentId}
            onChange={(e) => setClaimAgentId(e.target.value)}
            autoFocus
          />
          <TextField
            select
            label="Organización"
            value={claimOrganizationId}
            onChange={(e) => setClaimOrganizationId(e.target.value)}
          >
            {organizations.map((organization) => (
              <MenuItem key={organization.id} value={organization.id}>
                {organization.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Contraseña persistente"
            type="password"
            value={claimPassword}
            onChange={(e) => setClaimPassword(e.target.value)}
            helperText="Se usa sólo para verificar que podés adoptar este equipo."
          />
          {user?.role === 'admin' && (
            <Button onClick={handleGenerateClaimPassword} disabled={claimBusy}>
              Generar o rotar contraseña
            </Button>
          )}
          {claimError && <Alert severity="error">{claimError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClaimOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleClaim}
            disabled={claimBusy || !claimAgentId || !claimPassword || !claimOrganizationId}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
